import { useEffect, useState } from "react";
import useTasks from "../../../hooks/useTasks";
import { ETaskStatus, ITask } from "../../../data_models/task";
import { IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import LoadingIndicator from "../../common/LoadingIndicator";

import { EProjectStatus, IProject } from "../../../data_models/project";
import { FilledButton, OutlinedButton } from "../../common/Button";
import { getFreelancerName } from "../../../utils/freelancerUtils";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { EUserType } from "../../../data_models/enums";
import { DEFAULT_USER_PROFILE } from "../../../config/defaultAssetUrls";
import useUser from "../../../hooks/useUser";
import NamePlateFreelancer from "../../common/NamePlateFreelancer";
import IconButton from "../../common/IconButton";

import SmallAvatar from "../../common/SmallAvatar";
import { UserEnumTextMapper } from "../../../data_models/enumToTextMapper";
import SearchBar from "../../common/SearchBar";
import { AiOutlineUserAdd, AiOutlineUserDelete } from "react-icons/ai";
import useInvitations from "../../../hooks/useInvitations";
import { IoPersonAddOutline, IoPersonRemoveOutline } from "react-icons/io5";
import AvatarWithDetails from "../../common/AvatarWithDetails";
import { IInvitation, Invitation } from "../../../data_models/invitation";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import Tooltip from "../../common/Tooltip";

interface ShortlistedFreelancersLightboxProps {
    task: ITask;
    project: IProject;
    onClose: () => void;
    onTaskUpdate: (updatedTask: ITask) => void;
}

const ShortlistedFreelancersLightbox: React.FC<ShortlistedFreelancersLightboxProps> = ({ task, project, onClose, onTaskUpdate }) => {

    const { currentUserType } = useCurrentUser();
    const { fetchBriefFreelancersByIds, searchFreelancers: _searchFreelancers } = useUser();
    const { shortlistFreelancerToggle } = useTasks();
    const { newInvitation, fetchInvitationsForTask } = useInvitations();
    const { addSuccessNotification } = useDisplayNotifications();

    const [loading, setLoading] = useState(false);
    // freelancer id -> freelancer
    const [freelancers, setFreelancers] = useState<Map<string, IFreelancer>>(new Map());
    const [invitations, setInvitations] = useState<Invitation[]>();
    const [shortlistingFreelancerId, setShortlistingFreelancerId] = useState<string | null>(null);

    const [loadingInvitation, setLoadingInvitation] = useState<string[]>([]); // freelancer ids

    const [canInvite, setCanInvite] = useState<Map<string, boolean>>(new Map()); // freelancer id -> can invite
    const [invitationsAllowed, setInvitationsAllowed] = useState<boolean>(false);

    useEffect(() => {
        const ids = new Set([...task.freelancerShortlistByPm || [], ...task.freelancerShortlistByEmployer || []]);
        const idsToRemove: Set<string> = new Set(freelancers.keys());
        const newIdsToFetch: string[] = [];

        if (project && task) {
            if (project.status === EProjectStatus.IN_PROGRESS && (
                task.currentStatus === ETaskStatus.WAIT_FOR_FREELANCER ||
                task.currentStatus === ETaskStatus.REASSIGN
            )) {
                setInvitationsAllowed(true);
            } else {
                setInvitationsAllowed(false);
            }
        }

        // if ids contains a freelancer id that is not in the freelancers map, add it to the map and fetch the freelancer
        ids.forEach(id => {
            if (!freelancers.has(id)) {
                newIdsToFetch.push(id);
            }
        });

        idsToRemove.forEach(id => {
            if (ids.has(id)) {
                idsToRemove.delete(id);
            }
        });

        // in case there are new freelancers to fetch
        const newFreelancers = new Map<string, IFreelancer>(freelancers);
        if (newIdsToFetch.length > 0) {
            setLoading(true);
            fetchBriefFreelancersByIds(newIdsToFetch).then(freelancers => {
                freelancers.forEach(freelancer => {
                    newFreelancers.set(freelancer._id!, freelancer);
                });

                idsToRemove.forEach(id => {
                    newFreelancers.delete(id);
                });
                setFreelancers(newFreelancers);
            }).finally(() => {
                setLoading(false);
            });
        } else {
            // in case there are freelancers to remove but no new freelancers to fetch
            idsToRemove.forEach(id => {
                newFreelancers.delete(id);
            });
            setFreelancers(newFreelancers);
        }

        if (project.status === EProjectStatus.IN_PROGRESS) {
            fetchInvitationsForTask(task._id!, 1, 9999999).then(invitations => {
                setInvitations(invitations);

            }).catch(error => {
                console.error('error', error);
            });
        }
    }, [task, project])

    useEffect(() => {
        if (!invitations) return;
        if (freelancers.size === 0) return;

        const canInvite = new Map<string, boolean>();
        const freelancerIds = Array.from(freelancers.keys());

        freelancerIds.forEach(id => {
            canInvite.set(id, true);
        });

        for (const freelancerId of freelancerIds) {
            if (invitations.some(invitation => invitation.freelancerId === freelancerId)) {
                canInvite.set(freelancerId, false);
            }
        }
        setCanInvite(canInvite);
    }, [invitations, freelancers]);


    const onShortlist = async (freelancerId: string) => {
        setShortlistingFreelancerId(freelancerId);
        const updatedTask = await shortlistFreelancerToggle(task._id!, freelancerId);
        setShortlistingFreelancerId(null);
        if (updatedTask) {
            onTaskUpdate(updatedTask);
        }
    }

    const onInvite = (freelancerId: string) => {
        setLoadingInvitation(prev => [...prev, freelancerId]);
        newInvitation(freelancerId, task._id!, project._id!,).then(() => {
            const newCanInvite = new Map(canInvite);
            newCanInvite.set(freelancerId, false);
            setCanInvite(newCanInvite);
            setLoadingInvitation(prev => prev.filter(id => id !== freelancerId));
            addSuccessNotification(`Talent successfully invited to ${task.title}`);
        }).catch(() => {
            setLoadingInvitation(prev => prev.filter(id => id !== freelancerId));
        });
    }



    // Function to render each search result
    const renderSearchResult = (freelancer: IFreelancer): React.ReactNode => {

        return (
            <div className="w-full p-2 rounded-md hover:bg-neutral-100 cursor-pointer flex flex-row items-center space-x-4"
            >
                <SmallAvatar
                    imageUrl={freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                />
                <div className='flex flex-col'>
                    <p className='caption2 font-bold'>{getFreelancerName(freelancer)}</p>
                    <div className='flex flex-wrap gap-x-2'>{freelancer.roles &&
                        freelancer.roles!.map((role, index) =>
                            <div key={freelancer._id!} className='flex items-center gap-1'>
                                <p className='caption2'>{UserEnumTextMapper.freelancerRole(role)}</p>
                                {index < (freelancer.roles!.length - 1) && <div className='w-1 h-1 rounded-full bg-neutral-400' />}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-1 justify-end">
                    <FilledButton
                        onClick={() => {
                            onShortlist(freelancer._id!);
                        }}
                    >
                        Shortlist
                    </FilledButton>
                </div>
            </div>)
    };


    return (
        <Lightbox
            header={`${task.title}`}
            content={

                <div className="min-h-[500px]" >
                    {/* Search bar */}
                    <div className=''>

                        <SearchBar
                            searchFunction={_searchFreelancers}
                            renderResult={renderSearchResult}
                            placeholder="Search Freelancers..."
                        />
                    </div>
                    {(loading) ? <div><LoadingIndicator /></div>
                        : <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="font-semibold mb-4 text-gray-400">PM Shortlist</p>
                                <div className="flex flex-col gap-2">
                                    {task.freelancerShortlistByPm?.length === 0 ? (
                                        <p className="text-gray-500 italic">No talent shortlisted</p>
                                    ) : (
                                        task.freelancerShortlistByPm?.map(freelancerId =>
                                            freelancers.get(freelancerId) !== undefined ? (
                                                <div key={freelancerId} className="flex flex-row items-center gap-2">
                                                    <AvatarWithDetails
                                                        user={freelancers.get(freelancerId)!}
                                                        title="Talent"
                                                    />
                                                    {canInvite.get(freelancerId) && invitationsAllowed &&
                                                        <div>
                                                            <FilledButton
                                                                onClick={() => onInvite(freelancerId)}
                                                                icon={<IoPersonAddOutline />}
                                                                theme="primary"
                                                                size="small"
                                                                isLoading={loadingInvitation.includes(freelancerId)}
                                                            >
                                                                Invite
                                                            </FilledButton>
                                                        </div>}
                                                    {(currentUserType === EUserType.FREELANCER
                                                        || (currentUserType === EUserType.EMPLOYER && !task.freelancerShortlistByEmployer?.includes(freelancerId))
                                                    ) && <div className="ml-auto">
                                                            <Tooltip text={currentUserType === EUserType.FREELANCER
                                                                ? "Remove from shortlist"
                                                                : "Add to shortlist"}>
                                                                <IconButton
                                                                    onClick={() => onShortlist(freelancerId)}
                                                                    icon={currentUserType === EUserType.FREELANCER
                                                                        ? <IoPersonRemoveOutline />
                                                                        : <IoPersonAddOutline />}
                                                                    theme={currentUserType === EUserType.FREELANCER
                                                                        ? "danger"
                                                                        : "primary"}
                                                                    isLoading={shortlistingFreelancerId === freelancerId}
                                                                    disabled={shortlistingFreelancerId === freelancerId}
                                                                /></Tooltip></div>}
                                                </div>
                                            ) : (
                                                <div key={freelancerId} />
                                            )
                                        )
                                    )}
                                </div>
                            </div>
                            <div>
                                <p className="font-semibold mb-4 text-gray-400">Client Shortlist</p>
                                <div className="flex flex-col gap-2">
                                    {task.freelancerShortlistByEmployer?.length === 0 ? (
                                        <p className="text-gray-500 italic">No talent shortlisted</p>
                                    ) : (
                                        task.freelancerShortlistByEmployer?.map(freelancerId =>
                                            freelancers.get(freelancerId) !== undefined ? (
                                                <div id={freelancerId} className="flex flex-row items-center gap-2">
                                                    <AvatarWithDetails
                                                        user={freelancers.get(freelancerId)!}
                                                        title="Talent"
                                                    />
                                                    {canInvite.get(freelancerId) && invitationsAllowed &&
                                                        <div>
                                                            <FilledButton
                                                                onClick={() => onInvite(freelancerId)}
                                                                icon={<IoPersonAddOutline />}
                                                                theme="primary"
                                                                size="small"
                                                                isLoading={loadingInvitation.includes(freelancerId)}
                                                            >
                                                                Invite
                                                            </FilledButton>
                                                        </div>}
                                                    {(currentUserType === EUserType.EMPLOYER
                                                        || (currentUserType === EUserType.FREELANCER && !task.freelancerShortlistByPm?.includes(freelancerId))
                                                    ) && <div className="ml-auto">
                                                            <Tooltip text={currentUserType === EUserType.EMPLOYER
                                                                ? "Remove from shortlist"
                                                                : "Add to shortlist"}>
                                                                <IconButton
                                                                    onClick={() => onShortlist(freelancerId)}
                                                                    icon={currentUserType === EUserType.EMPLOYER
                                                                        ? <IoPersonRemoveOutline />
                                                                        : <IoPersonAddOutline />}
                                                                    theme={currentUserType === EUserType.EMPLOYER
                                                                        ? "danger"
                                                                        : "primary"}
                                                                    isLoading={shortlistingFreelancerId === freelancerId}
                                                                    disabled={shortlistingFreelancerId === freelancerId}
                                                                /></Tooltip></div>}
                                                </div>
                                            ) : (
                                                <div key={freelancerId} />
                                            )
                                        )
                                    )}
                                </div>
                            </div>
                        </div>}
                </div>
            }
            onClose={onClose}
        />
    );
}

export default ShortlistedFreelancersLightbox;